import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { useNavigationService } from '../../../../services/navigate-service';
import { ROUTES } from '../../../../config/routes';
import {
  useGetSavedCardsQuery,
  useSetDefaultCardMutation,
  usePlaceBulkOrderThroughCartMutation,
  useDeleteCartMutation,
  useDeleteCartItemsMutation,
  useDeleteAssetOrderMutation,
} from '../../services/orderApi';
import { transformBulkOrderCartData } from '../../cart/utils/cartTransforms';
import { usePromoCode } from './usePromoCode';
import type { SavedCard } from '../../../../types/order.types';
import type { PaymentPageProps } from '../payment.types';
import { buildOrderPayment } from '@/util/orderPayment';
import { useSkipCheckout } from '@/hooks/useEntityScope';

// Stamp a saved card onto a bulk-order payload. Pure helper — returns a new
// object so call sites can pass it to `setBulkOrderCartData` directly or
// through a functional updater.
function stampCardOnBulkOrder(
  data: Record<string, unknown>,
  card: SavedCard,
): Record<string, unknown> {
  const { payment: _payment, ...rest } = data;
  return {
    ...rest,
    nickName: card.nickName,
    cardNumber: card.cardNumber,
    cardType: card.cardType,
    payment: buildOrderPayment(card),
  };
}

export function usePayment(props: PaymentPageProps) {
  const {
    orderTotalAmount = 0, savingTotalAmount = 0, finalPaymentAmount,
    isBulkOrder = false, isSingleOrder: isSingleOrderProp = false,
  } = props;

  const location = useLocation();
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();

  // RTK Query
  const { data: cardsResponse, isLoading, refetch: refetchCards } = useGetSavedCardsQuery();
  const [setDefaultCardMutation] = useSetDefaultCardMutation();
  const [placeBulkOrder] = usePlaceBulkOrderThroughCartMutation();
  const [deleteCart] = useDeleteCartMutation();
  const [deleteCartItems] = useDeleteCartItemsMutation();
  const [deleteAssetOrder] = useDeleteAssetOrderMutation();

  // Memoized so the reference is stable when `cardsResponse` doesn't change;
  // otherwise the default-card effect's deps would invalidate on every render.
  const cardsData: SavedCard[] = useMemo(
    () => cardsResponse?.cards ?? [],
    [cardsResponse],
  );

  // Cart-mode is derived from the navigation state CartPage handed us.
  // No persistence: if the user lands directly on /payment (refresh, deep
  // link) location.state is empty and the redirect-on-missing-state effect
  // below bounces them back to /cart.
  const isCartOrder: boolean = location?.state?.isCartOrder ?? false;

  const isLogin = localStorage.getItem('isLogin');
  const skipCheckout = useSkipCheckout();
  const isSkipCheckOut = isLogin === null ? null : skipCheckout;

  const [selectedCard, setSelectedCard] = useState<SavedCard | null>(null);

  // Amounts
  const [subTotalAmount, setSubTotalAmount] = useState(
    Math.round((orderTotalAmount ?? 0) * 100) / 100 || 0
  );
  const [totalAmount, setTotalAmount] = useState(
    Math.round((orderTotalAmount ?? 0) * 100) / 100 || 0
  );
  const [savingsAmount, setSavingsAmount] = useState(
    Math.round((savingTotalAmount ?? 0) * 100) / 100 || 0
  );

  // Promo code (extracted hook)
  const promo = usePromoCode({
    subTotalAmount, totalAmount, isCartOrder, finalPaymentAmount,
    setTotalAmount, setSavingsAmount,
  });

  // Modals
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showUpdateCardModal, setShowUpdateCardModal] = useState(false);
  const [showDeleteCardModal, setShowDeleteCardModal] = useState(false);
  const [showMaxCardModal, setShowMaxCardModal] = useState(false);
  const [updateCardDetails, setUpdateCardDetails] = useState<SavedCard>();
  const [deleteCardDetails, setDeleteCardDetails] = useState<SavedCard>();

  // Bulk order
  const [bulkOrderCartData, setBulkOrderCartData] = useState<Record<string, unknown>>();
  const [submissionProcessing, setSubmissionProcessing] = useState(false);

  const isMaxCardCount = cardsData.length >= 5;
  const isSingleCard = cardsData.length <= 1;

  // If the user lands directly on /cart/payment (refresh, deep link, etc.)
  // location.state is empty and the order data we need to place the bulk
  // order is gone. Bounce them back to /cart with an explanation rather
  // than letting them stare at a payment page that can't submit.
  // Using useLayoutEffect so the redirect fires before paint — otherwise
  // the user sees a single frame of the (wrong) inline-mode chrome flash
  // before the navigation completes.
  useLayoutEffect(() => {
    const onPaymentRoute =
      window.location.pathname.includes('/cart/payment');
    const stateMissing =
      !location?.state || location.state.serviceTotalPrice == null;
    if (onPaymentRoute && stateMissing) {
      dispatch(
        showAlert({
          type: Variants.WARNING,
          message:
            'Your cart session expired. Please review your cart before checking out.',
        }),
      );
      navigateTo(ROUTES.ORDERS_CART);
    }
    // Run once on mount; we only care whether the user arrived with state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cart-data setup. Runs once when the user lands on /cart/payment. Does
  // not depend on cards: monthly-invoice customers (isSkipCheckOut=true)
  // have no saved cards but still need `bulkOrderCartData` populated so
  // the bulk-submit POST carries the cart contents.
  useEffect(() => {
    if (
      location?.state?.serviceTotalPrice == null ||
      !isCartOrder ||
      promo.promoCode !== null
    ) return;
    const transformed = transformBulkOrderCartData(location.state) as Record<string, unknown>;
    setBulkOrderCartData(transformed);
    setSubTotalAmount(Math.round(location.state.orderTotalPrice * 100) / 100);
    setTotalAmount(Math.round(location.state.orderTotalPrice * 100) / 100);
    setSavingsAmount(Math.round(location.state.totalSavingPrice * 100) / 100);
    // location.state / promo.promoCode / isCartOrder are signal-stable for
    // this flow's lifetime; relisting them would refire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default-card selection. Fires when the cards API resolves (and again
  // after handleSetDefault's refetchCards). Uses a functional updater so
  // the card stamp lands on whatever bulkOrderCartData the cart-data effect
  // produced, without coupling this effect's deps to that state.
  useEffect(() => {
    if (!cardsData.length) return;
    const defaultCard = cardsData.find((c) => c.default === true);
    if (!defaultCard) return;
    setSelectedCard(defaultCard);
    setBulkOrderCartData((prev) =>
      prev ? stampCardOnBulkOrder(prev, defaultCard) : prev,
    );
  }, [cardsData]);

  function isCardSelected(card: SavedCard): boolean {
    if (!selectedCard) return false;
    // DV-2789: match on the unique payment-profile id. Without the (removed) CVV, last4 + name +
    // expiry isn't unique — two profiles for one physical card, or coincident last4/expiry, would
    // both read as selected and could charge/highlight the wrong card. This id is exact.
    return selectedCard.customerPaymentProfileId === card.customerPaymentProfileId;
  }

  function handleCardClick(card: SavedCard) {
    if (isSkipCheckOut) return;
    setSelectedCard(card);
    setBulkOrderCartData((prev) =>
      prev ? stampCardOnBulkOrder(prev, card) : prev,
    );
  }

  async function handleSetDefault(card: SavedCard) {
    if (isSkipCheckOut) return;
    if (card.cardExpiryStatus === 'Expired') {
      dispatch(showAlert({ type: Variants.ERROR, message: 'Please select a valid card to set as default' }));
      return;
    }
    try {
      await setDefaultCardMutation({
        customerProfileId: card.customerProfileId,
        customerPaymentProfileId: card.customerPaymentProfileId,
      }).unwrap();
      refetchCards();
    } catch {
      dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to set default card' }));
    }
  }

  function openUpdateModal(card: SavedCard) {
    if (isSkipCheckOut) return;
    setUpdateCardDetails(card); setShowUpdateCardModal(true);
  }

  function openDeleteModal(card: SavedCard) {
    if (isSkipCheckOut) return;
    setDeleteCardDetails(card);
    if (isCardSelected(card)) setSelectedCard(null);
    setShowDeleteCardModal(true);
  }

  function addNewCard() {
    if (isSkipCheckOut) return;
    if (isMaxCardCount) setShowMaxCardModal(true);
    else setShowAddCardModal(true);
  }

  function isSubmitDisabled(): boolean {
    if (isSkipCheckOut) return false;
    return !(selectedCard || totalAmount === 0);
  }

  return {
    cardsData, isLoading, selectedCard, setSelectedCard,
    isCardSelected, handleCardClick, handleSetDefault, isMaxCardCount, isSingleCard,
    showAddCardModal, setShowAddCardModal, showUpdateCardModal, setShowUpdateCardModal,
    showDeleteCardModal, setShowDeleteCardModal, showMaxCardModal, setShowMaxCardModal,
    updateCardDetails, deleteCardDetails, openUpdateModal, openDeleteModal, addNewCard,
    subTotalAmount, totalAmount, savingsAmount,
    ...promo,
    isCartOrder, isSkipCheckOut, isBulkOrder, isSingleOrderProp,
    submissionProcessing, isSubmitDisabled, refetchCards,
    bulkOrderCartData, setSubmissionProcessing,
    placeBulkOrder, deleteCart, deleteCartItems, deleteAssetOrder, dispatch, navigateTo, location,
  };
}
